/**
 * Custom error classes for HydroApp
 * Provides type-safe error handling across the application
 */

/**
 * Base error class for all HydroApp errors
 */
export class HydroAppError extends Error {
  constructor(
    message: string,
    public code: string = 'UNKNOWN_ERROR',
    public details?: any
  ) {
    super(message);
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, HydroAppError.prototype);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      details: this.details,
    };
  }
}

/**
 * Notification-related errors
 */
export class NotificationError extends HydroAppError {
  constructor(message: string, code: string = 'NOTIFICATION_ERROR', details?: any) {
    super(message, code, details);
    Object.setPrototypeOf(this, NotificationError.prototype);
  }
}

/**
 * Permission-related errors
 */
export class PermissionDeniedError extends HydroAppError {
  constructor(message: string = 'Permission denied', details?: any) {
    super(message, 'PERMISSION_DENIED', details);
    Object.setPrototypeOf(this, PermissionDeniedError.prototype);
  }
}

/**
 * Storage-related errors
 */
export class StorageError extends HydroAppError {
  constructor(message: string, code: string = 'STORAGE_ERROR', details?: any) {
    super(message, code, details);
    Object.setPrototypeOf(this, StorageError.prototype);
  }
}

/**
 * Network-related errors
 */
export class NetworkError extends HydroAppError {
  constructor(message: string = 'Network error', details?: any) {
    super(message, 'NETWORK_ERROR', details);
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Validation-related errors
 */
export class ValidationError extends HydroAppError {
  constructor(
    message: string,
    public fields?: Record<string, string>,
    details?: any
  ) {
    super(message, 'VALIDATION_ERROR', details);
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Authentication-related errors
 */
export class AuthenticationError extends HydroAppError {
  constructor(message: string = 'Authentication failed', details?: any) {
    super(message, 'AUTHENTICATION_ERROR', details);
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

/**
 * Authorization-related errors
 */
export class AuthorizationError extends HydroAppError {
  constructor(message: string = 'Authorization failed', details?: any) {
    super(message, 'AUTHORIZATION_ERROR', details);
    Object.setPrototypeOf(this, AuthorizationError.prototype);
  }
}

/**
 * Hydration calculation errors
 */
export class HydrationError extends HydroAppError {
  constructor(message: string, code: string = 'HYDRATION_ERROR', details?: any) {
    super(message, code, details);
    Object.setPrototypeOf(this, HydrationError.prototype);
  }
}

/**
 * Type guard to check if error is a HydroAppError
 */
export function isHydroAppError(error: any): error is HydroAppError {
  return error instanceof HydroAppError;
}

/**
 * Type guard to check if error is a specific error type
 */
export function isErrorType<T extends HydroAppError>(
  error: any,
  errorClass: new (...args: any[]) => T
): error is T {
  return error instanceof errorClass;
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: any): string {
  if (error instanceof HydroAppError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
}

/**
 * Get error code for logging/analytics
 */
export function getErrorCode(error: any): string {
  if (error instanceof HydroAppError) {
    return error.code;
  }

  if (error instanceof Error) {
    return error.name;
  }

  return 'UNKNOWN_ERROR';
}

/**
 * Log error with context
 */
export function logError(error: any, context?: string): void {
  const message = getErrorMessage(error);
  const code = getErrorCode(error);
  const contextStr = context ? ` [${context}]` : '';

  console.error(`${code}${contextStr}: ${message}`, error);
}

export interface ApiFieldError {
  field: string;
  message: string;
}

export interface ApiErrorBody {
  message?: string;
  code?: string;
  errors?: ApiFieldError[];
}

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  phoneNumber: 'Phone number',
  avatarName: 'Avatar name',
  age: 'Age',
  gender: 'Gender',
  currentDailyWaterIntake: 'Daily water intake',
  currentDrinkingFrequency: 'Drinking frequency',
  reminderInterval: 'Reminder interval',
  dailyTarget: 'Daily goal',
  proratedDailyTarget: 'Daily goal',
  volumePerReminder: 'Volume per reminder',
  activeDays: 'Active days',
  activeHoursStart: 'Active hours start',
  activeHoursEnd: 'Active hours end',
  profilePictureUrl: 'Profile photo',
};

export function formatFieldLabel(field: string): string {
  if (!field) return 'Field';
  return (
    FIELD_LABELS[field] ||
    field
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim()
  );
}

export function formatApiErrorBody(body: ApiErrorBody | null | undefined): string {
  if (!body) return 'Something went wrong. Please try again.';

  if (body.errors && body.errors.length > 0) {
    return body.errors
      .map((error) => `${formatFieldLabel(error.field)}: ${error.message}`)
      .join('. ');
  }

  return body.message || 'Something went wrong. Please try again.';
}

export function parseApiErrorResponse(status: number, body: unknown): ValidationError {
  const apiBody = (typeof body === 'object' && body !== null ? body : {}) as ApiErrorBody;
  const fields =
    apiBody.errors?.reduce<Record<string, string>>((acc, error) => {
      acc[error.field] = error.message;
      return acc;
    }, {}) ?? undefined;

  return new ValidationError(formatApiErrorBody(apiBody), fields, {
    status,
    ...apiBody,
  });
}

export async function readApiErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text) as ApiErrorBody;
  } catch {
    return null;
  }
}
